import { getClientEnv, isSupabaseConfigured } from "@/lib/env";
import { i18n } from "@/i18n";
import { supabase } from "@/lib/supabase";
import {
  getRuntimePricingCatalogSnapshot,
  getRuntimeServicePricing,
} from "@/features/pricing/runtimePricing";
import {
  mapAiActionErrorBody,
  type AiActionErrorBody,
  AiActionError,
} from "@/features/ai/aiErrors";
import {
  mapContinuationSuggestionResponse,
  mapContinuationUsage,
  mapContinuityReviewResponse,
  mapEnhanceSelectionResponse,
  mapPolishChapterResponse,
  mapStructuralAnalysisResponse,
  mapStructuralAnalysisStatusResponse,
} from "@/features/ai/aiMappers";
import type {
  ContinuationSuggestionMutationResult,
  ContinuationUsageView,
  ContinuityReviewView,
  EnhanceSuggestionResult,
  PolishSuggestionResult,
  StructuralAnalysisCalloutReviewState,
  StructuralAnalysisDimensionName,
  StructuralAnalysisMutationResult,
  StructuralAnalysisStatusMutationResult,
} from "@/features/ai/ai.types";
import type { SubscriptionTier } from "@/types/projects";
import type { SupportedLocale } from "@/types/auth";
import type { ChapterView, StructuralAnalysisScopeSelection } from "@/types/story";

type ContinuationSuggestionResponse = {
  cacheStatus?: "hit" | "invalidated" | "miss" | "none" | null;
  chapterId: string;
  createdAt: string;
  id: string;
  platformRestriction?: {
    resetAt?: string | null;
    stage?: string | null;
  } | null;
  suggestions: unknown;
  stage?: string | null;
  truncated?: boolean | null;
  usage: {
    creditCost?: number | null;
    creditsRemaining?: number | null;
    tier?: SubscriptionTier | null;
    usesRemainingToday?: number | null;
  };
};

type ContinuityReviewResponse = {
  cacheStatus?: "hit" | "invalidated" | "miss" | "none" | null;
  chapterId: string;
  creditsRemaining?: number | null;
  issues?: Array<{
    citedSources?: unknown;
    description: string;
    id: string;
    resolution?: "acknowledged" | "dismissed" | "open" | "resolved" | null;
    severity: "critical" | "low" | "medium";
  }> | null;
  noCanonContext?: boolean | null;
  platformRestriction?: {
    resetAt?: string | null;
    stage?: string | null;
  } | null;
  retrievedCanonCount?: number | null;
  stage?: string | null;
  truncated?: boolean | null;
};

type StructuralAnalysisResponse = {
  cacheStatus?: "hit" | "invalidated" | "miss" | "none" | null;
  creditsRemaining?: number | null;
  platformRestriction?: {
    resetAt?: string | null;
    stage?: string | null;
  } | null;
  run: {
    chapterRange?: unknown;
    createdAt: string;
    creditsUsed: number;
    id: string;
    report?: unknown;
    storyId?: string | null;
  };
  stage?: string | null;
  truncated?: boolean | null;
};

type StructuralAnalysisStatusResponse = {
  dimensionName: StructuralAnalysisDimensionName;
  findingIndex: number;
  runId: string;
  status: StructuralAnalysisCalloutReviewState;
};

type EnhanceSelectionResponse = {
  chapterId: string;
  creditCost?: number | null;
  creditsRemaining?: number | null;
  noImprovements?: boolean | null;
  sourceContext?: unknown;
  suggestion?: string | null;
};

type PolishChapterResponse = {
  chapterId: string;
  creditCost?: number | null;
  creditsRemaining?: number | null;
  noImprovements?: boolean | null;
  originalText?: string | null;
  sourceContext?: unknown;
  suggestion?: string | null;
};

function getCurrentLocale(): SupportedLocale {
  const candidate = i18n.resolvedLanguage ?? i18n.language;

  return candidate === "es" || candidate === "pt" ? candidate : "en";
}

async function readAiActionErrorBody(error: unknown) {
  if (typeof error === "object" && error) {
    const response =
      ("context" in error ? (error as { context?: Response }).context : undefined)
      ?? ("response" in error ? (error as { response?: Response }).response : undefined);

    if (response) {
      try {
        return await response.json() as AiActionErrorBody;
      } catch {
        return null;
      }
    }
  }

  return null;
}

async function normalizeAiActionError(error: unknown) {
  if (error instanceof AiActionError) {
    return error;
  }

  const body = await readAiActionErrorBody(error);
  const fallbackMessage =
    error instanceof Error ? error.message : "AI_ACTION_FAILED";

  return mapAiActionErrorBody(body, fallbackMessage);
}

async function getAccessToken() {
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session?.access_token) {
    throw new AiActionError("access-denied", "PROJECT_ACCESS_DENIED");
  }

  return session.access_token;
}

async function invokeAiFunction<T>(
  name: string,
  body: Record<string, unknown>,
  method: "PATCH" | "POST" = "POST",
) {
  if (!isSupabaseConfigured()) {
    throw new AiActionError("failed", "AI_ACTION_FAILED");
  }

  const { supabaseFunctionsUrl, supabaseUrl } = getClientEnv();
  const response = await fetch(`${supabaseFunctionsUrl ?? supabaseUrl}/functions/v1/${name}`, {
    body: JSON.stringify(body),
    headers: {
      Authorization: `Bearer ${await getAccessToken()}`,
      "Content-Type": "application/json",
    },
    method,
  });

  const responseBody = await response.json().catch(() => null);

  if (!response.ok) {
    throw await normalizeAiActionError({
      context: {
        json: async () => responseBody,
      },
      message:
        typeof responseBody === "object" && responseBody && "code" in responseBody
          ? String(responseBody.code)
          : `AI_ACTION_FAILED_${response.status}`,
    });
  }

  return responseBody as T;
}

export async function loadContinuationUsage(
  tier: SubscriptionTier,
): Promise<ContinuationUsageView> {
  if (!isSupabaseConfigured()) {
    throw new AiActionError("failed", "AI_ACTION_FAILED");
  }

  if (tier === "free") {
    const usageDate = new Date().toISOString().slice(0, 10);
    const { data, error } = await supabase
      .from("daily_suggestion_counts")
      .select("count")
      .eq("usage_date", usageDate)
      .maybeSingle();

    if (error) {
      throw await normalizeAiActionError(error);
    }

    return mapContinuationUsage({
      creditCost: 0,
      tier,
      usesRemainingToday: Math.max(5 - (data?.count ?? 0), 0),
    });
  }

  const { data, error } = await supabase
    .from("credits")
    .select("balance")
    .maybeSingle();

  if (error) {
    throw await normalizeAiActionError(error);
  }

  return mapContinuationUsage({
    creditCost: getRuntimeServicePricing(
      "continuation_suggestion",
      getRuntimePricingCatalogSnapshot(),
    ).creditCost,
    creditsRemaining: data?.balance ?? 0,
    tier,
  });
}

export async function requestContinuationSuggestion(
  projectId: string,
  chapterId: string,
): Promise<ContinuationSuggestionMutationResult> {
  const response = await invokeAiFunction<ContinuationSuggestionResponse>(
    "continuation-suggestion",
    {
      chapterId,
      locale: getCurrentLocale(),
      projectId,
      useLiveModel: true,
    },
  );

  return mapContinuationSuggestionResponse(response);
}

export async function requestContinuityReview(
  projectId: string,
  chapterId: string,
): Promise<ContinuityReviewView> {
  const response = await invokeAiFunction<ContinuityReviewResponse>(
    "continuity-review",
    {
      chapterId,
      projectId,
      useLiveModel: true,
    },
  );

  return mapContinuityReviewResponse(projectId, response);
}

export async function requestStructuralAnalysis(
  projectId: string,
  storyId: string,
  scope: StructuralAnalysisScopeSelection,
  chapters: ChapterView[],
): Promise<StructuralAnalysisMutationResult> {
  const response = await invokeAiFunction<StructuralAnalysisResponse>(
    "structural-analysis",
    {
      projectId,
      scope,
      storyId,
      useLiveModel: true,
    },
  );

  return mapStructuralAnalysisResponse(projectId, chapters, response);
}

export async function updateStructuralFindingStatus(
  input: {
    dimensionName: StructuralAnalysisDimensionName;
    findingIndex: number;
    runId: string;
    status: StructuralAnalysisCalloutReviewState;
  },
): Promise<StructuralAnalysisStatusMutationResult> {
  const response = await invokeAiFunction<StructuralAnalysisStatusResponse>(
    "structural-analysis",
    input,
    "PATCH",
  );

  return mapStructuralAnalysisStatusResponse(response);
}

export async function requestSelectionEnhance(input: {
  chapterId: string;
  chapterTitle: string;
  paragraphContext: string;
  projectId: string;
  selectedText: string;
}): Promise<EnhanceSuggestionResult> {
  const response = await invokeAiFunction<EnhanceSelectionResponse>(
    "enhance-selection",
    {
      ...input,
      useLiveModel: true,
    },
  );

  return mapEnhanceSelectionResponse(response);
}

export async function requestChapterPolish(input: {
  chapterId: string;
  projectId: string;
}): Promise<PolishSuggestionResult> {
  const response = await invokeAiFunction<PolishChapterResponse>(
    "polish-chapter",
    {
      ...input,
      useLiveModel: true,
    },
  );

  return mapPolishChapterResponse(response);
}
